import {
  getEligibleCategoriesFromSubRoles,
  CONTACT_CATEGORY,
  isActiveProviderUser,
} from "./requestContactDesignations";
import { getUserType } from "./helperMethods";
import {
  extractEmailDomain,
  validateEmailDomainAgainstOrgUsers,
  formatPlatformRoleLabel,
  getUserSubRoleNames,
  contactSignature,
  normalizeOverviewContact,
  toRequestContactPayload,
  formatOverviewReadOnlyContact,
  pluralizeContactLabel,
  resolveSavedContactsFromDetails,
} from "./overviewContactsShared";

export {
  extractEmailDomain,
  validateEmailDomainAgainstOrgUsers,
  formatPlatformRoleLabel,
  getUserSubRoleNames,
  contactSignature,
  normalizeOverviewContact,
  toRequestContactPayload,
  formatOverviewReadOnlyContact,
  pluralizeContactLabel,
};

/** Vendor Admin or any RFI/VQQ Manager variant — not Audit/Assessment managers. */
export function isVqqEligibleVendorUser(user) {
  if (!isActiveProviderUser(user)) return false;
  const categories = getEligibleCategoriesFromSubRoles(getUserSubRoleNames(user));
  return categories.includes(CONTACT_CATEGORY.RFI);
}

/** Sponsor Admin or RFI/VQQ Manager variants. */
export function isVqqEligibleSponsorUser(user) {
  if (!isActiveProviderUser(user)) return false;
  const roles = getUserSubRoleNames(user);
  if (roles.some((r) => r === "Sponsor Admin" || r?.includes?.("Sponsor Admin"))) {
    return true;
  }
  if (roles.some((r) => r?.includes?.("RFI Manager"))) {
    return true;
  }
  const categories = getEligibleCategoriesFromSubRoles(roles);
  return categories.includes(CONTACT_CATEGORY.RFI);
}

export function mapOrgUsersToPickerContacts(users, { mode = "vendor" } = {}) {
  const eligibleFilter =
    mode === "sponsor" ? isVqqEligibleSponsorUser : isVqqEligibleVendorUser;

  return (users || [])
    .filter(eligibleFilter)
    .map((u, index) => normalizeOverviewContact(u, index))
    .filter(Boolean)
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return String(a.name).localeCompare(String(b.name));
    });
}

export function resolveSavedVendorContacts(rfisDetails) {
  return resolveSavedContactsFromDetails(rfisDetails, {
    listKeys: ["request_contacts", "requestContacts"],
    legacyEmailKeys: ["vendor_contact_email"],
  });
}

export function resolveSavedSponsorContacts(rfisDetails) {
  return resolveSavedContactsFromDetails(rfisDetails, {
    listKeys: ["sponsor_contacts", "sponsorContacts"],
    legacyEmailKeys: ["sponsor_contact_email"],
  });
}

export function isVqqContactsLocked(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return true;
  return s !== "sent" && s !== "pending";
}

/**
 * Role matrix for VQQ Overview contact fields (story 14534).
 * @param {string} userRole from useUserRole()
 */
export function getVqqOverviewContactPermissions(userRole, status) {
  const locked = isVqqContactsLocked(status);
  const userType = getUserType(userRole);
  const isAdmin = userType === "admin";
  const isSponsor = userType === "sponsor";
  const isVendor = userType === "vendor";

  return {
    userType,
    locked,
    canEditVendor: !locked && (isAdmin || isSponsor || isVendor),
    canEditSponsor: !locked && (isAdmin || isSponsor),
    showVendorPlatformRoles: isAdmin || isVendor,
    showSponsorPlatformRoles: true,
    useAddContactLabelsOnVendor: isSponsor,
  };
}

export const VQQ_MANAGER_CREATE_ROLES = {
  vendor: {
    provider_user_subrole: [{ Subrole: "RFI Manager" }],
    provider_user_permissions: [{ Permission: "RFI Requests" }],
  },
  sponsor: {
    sponsor_subrole_dto: [{ sub_role: "RFI Manager" }],
  },
};
